import { BodyText, Stack, Text, Title } from '@lessonnine/design-system.lib'
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json'
// skipcq: JS-0128 - deepsource is falsely flagging React as unused import 🤷
import React, { useCallback, useEffect, useState } from 'react'
import { getReturnToUrl } from 'src/dataLayer/localData/queryParams'
import { trackAuthenticationResetPassword } from 'src/dataLayer/remoteData/babbelEventStream'
import { CaptchaPasswordReset } from 'src/viewLayer/components/CaptchaPasswordReset/CaptchaPasswordReset'
import useSubmitPasswordReset from 'src/viewLayer/components/hooks/useSubmitPasswordReset'
import Footer from 'src/viewLayer/components/pages/CardLayout/Footer'
import PasswordInput from 'src/viewLayer/components/shared/Inputs/PasswordInput'
import TrackableButton from 'src/viewLayer/components/shared/TrackableButton'
import { t } from 'src/viewLayer/helpers/translations'

import {
  ResetPasswordPageCard,
  ResetPasswordPageContainer,
  ResetPasswordPageFormContainer,
} from '../../App/index.styled'
import NavBar from '../../App/NavBar'

const STATUS_IDLE = 'idle'
const STATUS_LOADING = 'loading'
const STATUS_SUCCESS = 'success'

export default function PasswordResetPage() {
  const [resetPasswordRequestStatus, setResetPasswordRequestStatus] = useState(STATUS_IDLE)
  const [captchaToken, setCaptchaToken] = useState()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errorKey, setErrorKey] = useState('')
  const [passwordResetToken, setPasswordResetToken] = useState()
  const [isButtonClicked, setIsButtonClicked] = useState(false)
  const { isResetPasswordRequestInProgress, makeResetPasswordRequest } = useSubmitPasswordReset({
    captchaResponseToken: captchaToken,
    confirmPassword,
    password,
    passwordResetToken,
  })
  const parameters = new URLSearchParams(globalThis.location.search)
  const isActionInProgress =
    isResetPasswordRequestInProgress ||
    resetPasswordRequestStatus === STATUS_LOADING ||
    isButtonClicked
  const isPasswordMatching = () =>
    password && confirmPassword && password === confirmPassword && password.length >= 6

  useEffect(() => {
    const passwordResetTokenParameter = parameters.get('token')
    if (passwordResetTokenParameter) {
      setPasswordResetToken(passwordResetTokenParameter)
    }
    trackAuthenticationResetPassword({ stage: 'reset-page-shown' })
  }, [])

  useEffect(() => {
    if (resetPasswordRequestStatus === STATUS_SUCCESS) {
      trackAuthenticationResetPassword({ stage: 'success-page-shown' })
    }
  }, [resetPasswordRequestStatus])

  useEffect(() => {
    if (!captchaToken || !isButtonClicked) {
      return
    }
    setIsButtonClicked(false)
    ;(async () => {
      setResetPasswordRequestStatus(STATUS_LOADING)
      setErrorKey('')

      const result = await makeResetPasswordRequest({
        captchaResponseToken: captchaToken,
        confirmPassword,
        password,
        passwordResetToken,
      })
      trackAuthenticationResetPassword({ stage: 'submitted' })

      if (result.success) {
        setResetPasswordRequestStatus(STATUS_SUCCESS)
      } else {
        setErrorKey(result.error || 'An error occurred')
        setResetPasswordRequestStatus(STATUS_IDLE)
        setPassword('')
        setConfirmPassword('')
        setCaptchaToken('')
      }
    })()
  }, [captchaToken, isButtonClicked, makeResetPasswordRequest])

  const handleResetPassword = useCallback(() => {
    setIsButtonClicked(true)
  }, [])

  const redirectToDashboard = useCallback(() => {
    const redirectUrl = getReturnToUrl(globalThis.location.search, false)
    globalThis.location.replace(redirectUrl)
  }, [])

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value)
  }, [])

  const handleConfirmPasswordChange = useCallback((e) => {
    setConfirmPassword(e.target.value)
  }, [])
  return (
    <>
      <NavBar />
      <ResetPasswordPageContainer>
        <ResetPasswordPageCard gap={24} padding={{ small: [80, 8, 24], xxsmall: [36, 8, 10] }}>
          <Title size="small" textAlign="center">
            {resetPasswordRequestStatus === 'success'
              ? t('change_password_page.reset_password_title')
              : t('change_password_page.title')}
          </Title>
          {resetPasswordRequestStatus === 'success' ? (
            <>
              <Text textAlign="center">{t('change_password_page.success_confirmation')}</Text>
              <TrackableButton
                disabled={isActionInProgress}
                fullWidth
                onClick={redirectToDashboard}
                variant="primary"
              >
                {t('change_password_page.button_ok')}
              </TrackableButton>
            </>
          ) : (
            <>
              <Text textAlign="center">{t('change_password_page.description')}</Text>
              <ResetPasswordPageFormContainer gap={24}>
                <Stack alignItems="center" gap={15} padding={18}>
                  <PasswordInput
                    disabled={isActionInProgress}
                    id="uat-password-input-field"
                    isFocused
                    label={t('password.placeholder')}
                    onChangeHandler={handlePasswordChange}
                    placeholder={t('change_password_page.placeholder_password_input')}
                    value={password}
                  />
                  <PasswordInput
                    disabled={isActionInProgress}
                    id="uat-confirm-password-input-field"
                    label={t('change_password_page.change_password_confirm')}
                    onChangeHandler={handleConfirmPasswordChange}
                    placeholder={t('change_password_page.placeholder_password_confirmation_input')}
                    value={confirmPassword}
                  />
                  {errorKey && (
                    <BodyText
                      as="p"
                      color={tokens.color.theme.dark.reference.red.red60}
                      size="small"
                      textAlign="center"
                    >
                      {t(errorKey)}
                    </BodyText>
                  )}
                  <TrackableButton
                    dataTestid="forgot-password-page-submit-button"
                    disabled={isActionInProgress || !isPasswordMatching()}
                    fullWidth
                    id="uat-forgot-password-button"
                    inProgress={isActionInProgress}
                    onClick={handleResetPassword}
                    title="Reset password button"
                    trackingId="reset_password_submit"
                    variant="primary"
                  >
                    {t('change_password_page.button_change_password')}
                  </TrackableButton>
                </Stack>
              </ResetPasswordPageFormContainer>
            </>
          )}
        </ResetPasswordPageCard>
        <CaptchaPasswordReset
          setCaptchaToken={setCaptchaToken}
          shouldExecuteCaptcha={isButtonClicked}
          shouldTrackTokenRequested={false}
        />
        <Footer />
      </ResetPasswordPageContainer>
    </>
  )
}
